import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useUserRole } from '../hooks/useUserRole';
import type { UserDeal } from '../types/database';

interface DealContextType {
  selectedDeal: UserDeal | null;
  availableDeals: UserDeal[];
  isLoading: boolean;
  error: string | null;
  selectDeal: (deal: UserDeal) => void;
  selectedScenarioId: string | null;
  selectScenario: (scenarioId: string | null) => void;
  refreshDeals: () => Promise<void>;
  clearSelectedDeal: () => void;
}

const DealContext = createContext<DealContextType | undefined>(undefined);

interface DealProviderProps {
  children: ReactNode;
}

export function DealProvider({ children }: DealProviderProps) {
  const { user } = useUser();
  const { userProfile, isLoading: isUserProfileLoading } = useUserRole();
  const [selectedDeal, setSelectedDeal] = useState<UserDeal | null>(null);
  const [availableDeals, setAvailableDeals] = useState<UserDeal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false); // Guard to prevent multiple simultaneous calls
  const [error, setError] = useState<string | null>(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const shouldRestoreFromStorage = useRef(true);

  // Load available deals for the user - only once when ready
  const loadDeals = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    
    // Wait for user profile to be loaded before loading deals
    if (isUserProfileLoading || !userProfile) {
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (isLoadingDeals) {
      return;
    }
    
    setIsLoadingDeals(true);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await DatabaseService.getUserDeals('read', user.id);
      
      if (response.data) {
        setAvailableDeals(response.data);
        
        // Only restore from localStorage on initial load, not on subsequent calls
        // This prevents overriding manual selections from the dropdown
        if (shouldRestoreFromStorage.current) {
          const lastSelectedDealId = localStorage.getItem('lastSelectedDealId');
          if (lastSelectedDealId) {
            const lastDeal = response.data.find(deal => deal.deal_id === lastSelectedDealId);
            if (lastDeal) {
              setSelectedDeal(lastDeal);
            }
          }
          shouldRestoreFromStorage.current = false;
        }
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deals');
    } finally {
      setIsLoading(false);
      setIsLoadingDeals(false);
    }
  }, [user?.id, isUserProfileLoading, userProfile]); // Removed isLoadingDeals from dependencies to prevent infinite loop

  // Select a deal and persist the selection
  const selectDeal = (deal: UserDeal) => {
    setSelectedDeal(deal);
    localStorage.setItem('lastSelectedDealId', deal.deal_id);
    shouldRestoreFromStorage.current = false; // Prevent future restores from localStorage

    // Restore last scenario for this deal if present
    const lastScenarioKey = `lastSelectedScenarioId_${deal.deal_id}`;
    const lastScenarioId = localStorage.getItem(lastScenarioKey);
    setSelectedScenarioId(lastScenarioId);
  };

  // Clear selected deal
  const clearSelectedDeal = () => {
    setSelectedDeal(null);
    setSelectedScenarioId(null);
    localStorage.removeItem('lastSelectedDealId');
    shouldRestoreFromStorage.current = true; // Allow future restores from localStorage
  };

  // Select a scenario within the current deal and persist per-deal
  const selectScenario = (scenarioId: string | null) => {
    setSelectedScenarioId(scenarioId);
    const dealId = selectedDeal?.deal_id;
    if (dealId) {
      const key = `lastSelectedScenarioId_${dealId}`;
      if (scenarioId) {
        localStorage.setItem(key, scenarioId);
      } else {
        localStorage.removeItem(key);
      }
    }
  };

  // Refresh deals list
  const refreshDeals = useCallback(async () => {
    await loadDeals();
  }, [loadDeals]);

  // Load deals only once when user and profile are ready
  useEffect(() => {
    if (user?.id && userProfile) {
      loadDeals();
    } else if (!user?.id) {
      // Clear state when user logs out
      setAvailableDeals([]);
      setSelectedDeal(null);
      setSelectedScenarioId(null);
      setError(null);
    }
    // Only trigger when user ID changes or when userProfile becomes available
    // Removed isUserProfileLoading from dependencies because it toggles constantly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, !!userProfile]);

  // Update selectedDeal when availableDeals changes (e.g., after a deal update)
  useEffect(() => {
    if (selectedDeal && availableDeals.length > 0) {
      const updatedDeal = availableDeals.find(deal => deal.deal_id === selectedDeal.deal_id);
      if (updatedDeal && updatedDeal !== selectedDeal) {
        setSelectedDeal(updatedDeal);
      }
    } else if (selectedDeal && availableDeals.length === 0) {
      // If no deals are available, clear the selected deal
      setSelectedDeal(null);
      setSelectedScenarioId(null);
    } else if (selectedDeal && !availableDeals.find(deal => deal.deal_id === selectedDeal.deal_id)) {
      // If the selected deal is no longer in the available deals (e.g., deleted), clear it
      setSelectedDeal(null);
      setSelectedScenarioId(null);
    }
  }, [availableDeals]); // Removed selectedDeal from dependency array to prevent infinite loop

  // NDA info is now handled by individual components (DealManager, DealSelector)

  const value: DealContextType = {
    selectedDeal,
    availableDeals,
    isLoading,
    error,
    selectDeal,
    selectedScenarioId,
    selectScenario,
    refreshDeals,
    clearSelectedDeal,
  };

  return (
    <DealContext.Provider value={value}>
      {children}
    </DealContext.Provider>
  );
}

export function useDeal() {
  const context = useContext(DealContext);
  if (context === undefined) {
    throw new Error('useDeal must be used within a DealProvider');
  }
  return context;
}


